////////////////////////////////////////
// IMPORTING MODULES
////////////////////////////////////////

import { useRef, useState, useEffect, Suspense, useMemo } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import {
  Mesh,
  TextureLoader,
  Vector3,
  CubicBezierCurve3,
  TubeGeometry,
  MeshStandardMaterial,
  ShaderMaterial,
  Texture,
} from "three";
import { AIRPORT_DICTIONARY_INDEX } from "../data/airports";

interface Airport {
  id: string;
  name: string;
  city: string;
  country: string;
  airport_code: string;
  visited_at?: string;
  aircraft?: string;
  notes?: string;
}

interface GlobeProps {
  globeMesh:
    | "nightMesh"
    | "dayMesh"
    | "localTimeMesh"
    | "timeZoneMesh"
    | "noMesh";
  visitedAirports: Airport[];
  onAirportClick?: (airport: Airport) => void;
  userSettings: any;
}

interface CountryInfo {
  name: string;
  capital: string[];
  population: number;
  region: string;
  subregion: string;
  flag: string;
  languages: { [key: string]: string };
  currencies: { [key: string]: { name: string; symbol: string } };
}

// ================= GlobeComponent =================
function GlobeComponent({
  visitedAirports,
  onAirportClick,
  userSettings,
  earthTexture,
  globeMesh,
}: GlobeProps & { earthTexture: any }) {
  const [cameraDistance, setCameraDistance] = useState(6);
  const [cameraPosition, setCameraPosition] = useState(new Vector3(0, 0, 6));
  const [selectedAirport, setSelectedAirport] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [countryInfo, setCountryInfo] = useState<CountryInfo | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [popupScale, setPopupScale] = useState(0);
  const [popupVisible, setPopupVisible] = useState(false);

  const globeRef = useRef<Mesh>(null);
  const materialRef = useRef<any>();

  // Animate popup when airport selected
  useEffect(() => {
    if (selectedAirport) {
      setPopupScale(0);
      setPopupVisible(true);
      setTimeout(() => setPopupScale(1), 10);
    } else {
      setPopupScale(0);
      setTimeout(() => setPopupVisible(false), 300);
    }
  }, [selectedAirport]);

  // Format date nicely
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Fetch country info from REST Countries API
  // const fetchCountryInfo = async (countryName: string) => {
  //   if (!countryName || countryName === "undefined") {
  //     console.warn("No country name provided");
  //     setCountryInfo(null);
  //     setIsLoadingInfo(false);
  //     return;
  //   }
  //
  //   setIsLoadingInfo(true);
  // };

  // Handle airport click
  // Helper function to get airport details by code
  const getAirportDetails = (airportCode: string) => {
    return AIRPORT_DICTIONARY_INDEX[airportCode];
  };

  // Check if a point on the globe is facing the camera
  const isPointVisible = (
    position: [number, number, number],
    camPos: Vector3,
  ) => {
    const pointVector = new Vector3(...position);
    const normalVector = pointVector.clone().normalize();
    const cameraDirection = pointVector.clone().sub(camPos).normalize();
    return normalVector.dot(cameraDirection) < 0;
  };

  // Convert lat/lng → 3D coordinates
  const latLngToVector3 = (lat: number, lng: number, radius: number = 2.5) => {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lng + 180) * (Math.PI / 180);
    const x = -(radius * Math.sin(phi) * Math.cos(theta));
    const z = radius * Math.sin(phi) * Math.sin(theta);
    const y = radius * Math.cos(phi);
    return [x, y, z] as [number, number, number];
  };

  // Create pins from airports
  const allPins = visitedAirports
    .map((airport: any) => {
      let lat = airport.lat;
      let lng = airport.lng;

      if (!lat || !lng) {
        const airportDetails = getAirportDetails(airport.airport_code);
        if (airportDetails) {
          lat = lat || airportDetails.lat;
          lng = lng || airportDetails.lng;
        }
      }

      if (!lat || !lng) {
        console.warn(
          `No coordinates found for airport: ${
            airport.airport_code || airport.name
          }`,
        );
        return null;
      }

      return {
        position: latLngToVector3(lat, lng),
        airport,
        airportDetails: getAirportDetails(airport.airport_code),
      };
    })
    .filter((pin): pin is NonNullable<typeof pin> => pin !== null);

  // LOD System
  const pins = useMemo(() => {
    const totalPins = allPins.length;
    if (totalPins <= 20) return allPins;
    if (cameraDistance > 12) return allPins.slice(0, 10);
    else if (cameraDistance > 8) return allPins.slice(0, 30);
    else if (cameraDistance > 6) return allPins.slice(0, 60);
    else return allPins.slice(0, 100);
  }, [allPins, cameraDistance]);

  // Visible pins (facing camera)
  // const visiblePins = useMemo(() => {
  //   return pins.filter((pin) => isPointVisible(pin.position, cameraPosition));
  // }, [pins, cameraPosition]);

  // Check if selected airport popup should be visible
  const isSelectedAirportVisible = useMemo(() => {
    if (!selectedAirport) return false;
    return isPointVisible(selectedAirport.position, cameraPosition);
  }, [selectedAirport, cameraPosition]);

  // Track camera
  useFrame(({ camera }) => {
    setCameraPosition(camera.position.clone());
    const distance = camera.position.length();
    if (Math.abs(distance - cameraDistance) > 0.1) {
      setCameraDistance(distance);
    }

    if (globeMesh === "timeZoneMesh" && materialRef.current) {
      const now = new Date();
      const utcHours = now.getUTCHours() + now.getUTCMinutes() / 60;
      materialRef.current.uniforms.time.value = utcHours;
    }
  });

  // Shader for timezone mesh
  const shaderMaterial =
    globeMesh === "timeZoneMesh"
      ? new ShaderMaterial({
          uniforms: {
            map: { value: earthTexture },
            time: {
              value: new Date().getUTCHours() + new Date().getUTCMinutes() / 60,
            },
          },
          vertexShader: `
            varying vec2 vUv;
            varying vec3 vNormal;
            void main() {
              vUv = uv;
              vNormal = normal;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
            }
          `,
          fragmentShader: `
            uniform sampler2D map;
            uniform float time;
            varying vec2 vUv;
            varying vec3 vNormal;
            void main() {
              vec3 color = texture2D(map, vUv).rgb;
              float lon = atan(vNormal.z, vNormal.x);
              float sunAngle = (time / 24.0) * 6.2831853;
              float brightness = clamp(cos(lon - sunAngle) * 0.5 + 0.5, 0.0, 1.0);
              vec3 nightColor = color * 0.1;
              vec3 dayColor = color;
              gl_FragColor = vec4(mix(nightColor, dayColor, brightness), 1.0);
            }
          `,
        })
      : null;

  let materialToUse: any;
  if (globeMesh === "noMesh") {
    materialToUse = new MeshStandardMaterial({ color: "black" });
  } else if (globeMesh === "timeZoneMesh") {
    materialToUse = shaderMaterial;
  } else {
    materialToUse = new MeshStandardMaterial({ map: earthTexture });
  }

  return (
    <group rotation={[0, 9, 0]} position={[0, -0.5, 0]}>
      {/* Globe */}
      <mesh ref={globeRef}>
        <sphereGeometry args={[2.5, 64, 64]} />
        <primitive ref={materialRef} object={materialToUse} attach="material" />
      </mesh>

      {/* Gridlines */}
      {userSettings.showGridlines && (
        <mesh>
          <sphereGeometry args={[2.52, 32, 32]} />
          <meshBasicMaterial
            color="#00bcd4"
            transparent
            opacity={0.1}
            wireframe
          />
        </mesh>
      )}

      {/* Airport Pins */}
      {pins.map((pin) => {
        const isSimpleMode = cameraDistance > 8;
        const showGlow = cameraDistance < 8;
        const showLabels = cameraDistance < 10;

        return (
          <group
            key={`${pin.airport.id}-${userSettings.airportsColor}`}
            position={pin.position}
          >
            <mesh
              onPointerOver={(e) => {
                e.stopPropagation();
                document.body.style.cursor = "pointer";
              }}
              onPointerOut={() => {
                document.body.style.cursor = "auto";
              }}
            >
              <sphereGeometry
                args={isSimpleMode ? [0.03, 6, 6] : [0.04, 8, 8]}
              />
              <meshBasicMaterial
                color={userSettings.airportsColor || "#00bcd4"}
              />
            </mesh>

            {showGlow && (
              <mesh>
                <sphereGeometry args={[0.06, 8, 8]} />
                <meshBasicMaterial
                  color={userSettings.airportsColor || "#00bcd4"}
                  transparent
                  opacity={0.2}
                />
              </mesh>
            )}

            {showLabels && !selectedAirport && (
              <Html
                position={[0, 0.1, 0]}
                center
                style={{
                  color: userSettings.airportsColor || "#00bcd4",
                  fontSize: "10px",
                  fontWeight: "bold",
                  pointerEvents: "none",
                  textShadow: `0 0 10px ${
                    userSettings.airportsColor || "#00bcd4"
                  }`,
                }}
              >
                {pin.airportDetails?.city || pin.airport.city}
              </Html>
            )}
          </group>
        );
      })}

      {/* Popup - only render when visible on front half */}
      {selectedAirport && popupVisible && isSelectedAirportVisible && (
        <Html
          position={[
            selectedAirport.position[0] * 1.3,
            selectedAirport.position[1] * 1.3,
            selectedAirport.position[2] * 1.3,
          ]}
          center
          distanceFactor={4}
        >
          <div
            style={{
              transform: `scale(${popupScale})`,
              transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
              background: "rgba(20, 24, 38, 0.9)",
              border: `1px solid ${userSettings.airportsColor}`,
              borderRadius: "14px",
              padding: "18px 20px",
              minWidth: "300px",
              maxWidth: "360px",
              color: "#e4f9ff",
              fontFamily: "Inter, system-ui, sans-serif",
              boxShadow: `0 8px 24px ${userSettings.airportsColor}33, inset 0 0 15px ${userSettings.airportsColor}`,
              backdropFilter: "blur(12px)",
              position: "relative",
              overflow: "hidden",
              opacity: popupScale,
            }}
          >
            {/* Animated glow background */}
            <div
              style={{
                position: "absolute",
                top: "-40%",
                left: "-40%",
                width: "180%",
                height: "180%",
                background: `radial-gradient(circle at center, ${userSettings.airportsColor}1a 0%, transparent 70%)`,
                animation: "glowmove 5s ease-in-out infinite",
                pointerEvents: "none",
              }}
            />
            <style>
              {`
          @keyframes glowmove {
            0%, 100% { transform: translate(0,0) scale(1); opacity: 0.5; }
            50% { transform: translate(5%, -5%) scale(1.1); opacity: 1; }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
            </style>

            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "14px",
                position: "relative",
                zIndex: 2,
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 700,
                    letterSpacing: "0.3px",
                    color: userSettings.airportsColor,
                  }}
                >
                  {selectedAirport.airportDetails?.city ??
                    selectedAirport.airport?.city ??
                    "Unknown City"}
                </h3>
                <p
                  style={{
                    margin: "2px 0",
                    fontSize: "13px",
                    color: userSettings.airportsColor,
                    fontWeight: 600,
                  }}
                >
                  {selectedAirport.airport?.airport_code ?? "N/A"}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    color: "#9fc9c5",
                    fontStyle: "italic",
                  }}
                >
                  {selectedAirport.airportDetails?.name ??
                    selectedAirport.airport?.name ??
                    ""}
                </p>
              </div>

              {/* Close button */}
              <div
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  zIndex: 3,
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAirport(null);
                  }}
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "50%",
                    color: "#e4f9ff",
                    fontSize: "18px",
                    cursor: "pointer",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background 0.25s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Visit Details */}
            <div
              style={{
                background: `${userSettings.airportsColor}14`,
                borderRadius: "10px",
                padding: "10px 14px",
                marginBottom: "14px",
                border: `1px solid ${userSettings.airportsColor}`,
                zIndex: 2,
                position: "relative",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  color: userSettings.airportsColor,
                  fontWeight: 700,
                  marginBottom: "8px",
                  letterSpacing: "1px",
                }}
              >
                ✈ VISIT DETAILS
              </div>

              <div style={{ fontSize: "13px", lineHeight: 1.5 }}>
                {selectedAirport.airport?.visited_at && (
                  <div>
                    <strong style={{ color: userSettings.airportsColor }}>
                      Last Visited:
                    </strong>{" "}
                    {formatDate(selectedAirport.airport.visited_at)}
                  </div>
                )}
                {selectedAirport.airport?.aircraft && (
                  <div>
                    <strong style={{ color: userSettings.airportsColor }}>
                      Aircraft:
                    </strong>{" "}
                    {selectedAirport.airport.aircraft}
                  </div>
                )}
                {selectedAirport.airport?.notes && (
                  <div>
                    <strong style={{ color: userSettings.airportsColor }}>
                      Notes:
                    </strong>{" "}
                    {selectedAirport.airport.notes}
                  </div>
                )}
                {!selectedAirport.airport?.visited_at &&
                  !selectedAirport.airport?.aircraft &&
                  !selectedAirport.airport?.notes && (
                    <div style={{ color: "#889b9b", fontStyle: "italic" }}>
                      No visit details recorded
                    </div>
                  )}
              </div>
            </div>

            {/* Country Info */}
            <div
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "10px",
                padding: "12px 16px",
                color: "#b4e8e4",
                fontSize: "13px",
                zIndex: 2,
                position: "relative",
              }}
            >
              {isLoadingInfo ? (
                <div style={{ textAlign: "center", color: "#a5bdbd" }}>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      border: `3px solid ${userSettings.airportsColor}33`,
                      borderTop: `3px solid ${userSettings.airportsColor}`,
                      borderRadius: "50%",
                      margin: "0 auto 8px",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  Loading country info…
                </div>
              ) : countryInfo ? (
                <>
                  {/* Flag */}
                  {countryInfo.flag && (
                    <div style={{ textAlign: "center", marginBottom: "10px" }}>
                      <img
                        src={countryInfo.flag}
                        alt={`${countryInfo.name} flag`}
                        style={{
                          width: "80px",
                          height: "auto",
                          borderRadius: "6px",
                          border: `1px solid ${userSettings.airportsColor}4d`,
                          boxShadow: `0 0 10px ${userSettings.airportsColor}33`,
                        }}
                      />
                    </div>
                  )}

                  <div
                    style={{
                      lineHeight: 1.6,
                      color: userSettings.airportsColor,
                    }}
                  >
                    <p style={{ margin: 0 }}>
                      <strong>Country:</strong>{" "}
                      <span style={{ color: "#a5bdbd" }}>
                        {countryInfo.name ?? "Unknown Country"}
                      </span>
                    </p>

                    {countryInfo.capital?.length > 0 && (
                      <p style={{ margin: "4px 0 0", color: "#a5bdbd" }}>
                        <strong style={{ color: userSettings.airportsColor }}>
                          Capital:
                        </strong>{" "}
                        {countryInfo.capital.join(", ")}
                      </p>
                    )}

                    {countryInfo.population && (
                      <p style={{ margin: "4px 0 0", color: "#a5bdbd" }}>
                        <strong style={{ color: userSettings.airportsColor }}>
                          Population:
                        </strong>{" "}
                        {countryInfo.population.toLocaleString()}
                      </p>
                    )}

                    {countryInfo.region && (
                      <p style={{ margin: "4px 0 0", color: "#a5bdbd" }}>
                        <strong style={{ color: userSettings.airportsColor }}>
                          Region:
                        </strong>{" "}
                        {countryInfo.region}
                        {countryInfo.subregion && ` (${countryInfo.subregion})`}
                      </p>
                    )}

                    {countryInfo.languages &&
                      Object.keys(countryInfo.languages).length > 0 && (
                        <p style={{ margin: "4px 0 0", color: "#a5bdbd" }}>
                          <strong style={{ color: userSettings.airportsColor }}>
                            Languages:
                          </strong>{" "}
                          {Object.values(countryInfo.languages).join(", ")}
                        </p>
                      )}

                    {countryInfo.currencies &&
                      Object.keys(countryInfo.currencies).length > 0 && (
                        <p style={{ margin: "4px 0 0", color: "#a5bdbd" }}>
                          <strong style={{ color: userSettings.airportsColor }}>
                            Currency:
                          </strong>{" "}
                          {Object.values(countryInfo.currencies)
                            .map((c) => `${c.name} (${c.symbol})`)
                            .join(", ")}
                        </p>
                      )}
                  </div>
                </>
              ) : (
                <div style={{ color: "#a5bdbd", fontStyle: "italic" }}>
                  Country info unavailable
                </div>
              )}
            </div>
          </div>
        </Html>
      )}

      {/* Travel Arcs */}
      {cameraDistance < 15 &&
        allPins.length > 1 &&
        allPins
          .slice(0, Math.min(allPins.length, cameraDistance > 10 ? 20 : 50))
          .map((pin, index) => {
            if (index === 0) return null;
            const prevPin = allPins[index - 1];
            const start = new Vector3(...prevPin.position);
            const end = new Vector3(...pin.position);
            const midPoint = new Vector3()
              .addVectors(start, end)
              .multiplyScalar(0.5);
            const dir = midPoint.clone().normalize();
            const control1 = start.clone().add(dir.clone().multiplyScalar(2.5));
            const control2 = end.clone().add(dir.clone().multiplyScalar(2.5));
            const curve = new CubicBezierCurve3(start, control1, control2, end);
            const segments = cameraDistance > 10 ? 20 : 40;
            const tubeGeometry = new TubeGeometry(
              curve,
              segments,
              0.015,
              8,
              false,
            );

            return (
              <mesh key={`path-${index}`} geometry={tubeGeometry}>
                <meshBasicMaterial
                  color={userSettings.connectionsColor || "#ff9800"}
                  transparent
                  opacity={cameraDistance > 10 ? 0.6 : 0.9}
                />
              </mesh>
            );
          })}
    </group>
  );
}

// ================= GlobeWithTextures - INSIDE SUSPENSE =================
function GlobeWithTextures({
  visitedAirports,
  onAirportClick,
  globeMesh,
  userSettings,
}: GlobeProps) {
  // Load textures INSIDE Suspense boundary
  // const dayTexture = useLoader(TextureLoader, "/textures/earthmap_day.jpg");
  //const nightTexture = useLoader(TextureLoader, "/textures/earthmap_night.jpg");
  const dayTexture = useLoader(TextureLoader, "/textures/earthmap_day.jpg");
  const nightTexture = useLoader(TextureLoader, "/textures/earthmap_night.jpg");
  const [earthTexture, setEarthTexture] = useState<Texture | null>(null);

  useEffect(() => {
    if (globeMesh === "nightMesh") {
      setEarthTexture(nightTexture);
    } else if (globeMesh === "dayMesh") {
      setEarthTexture(dayTexture);
    } else if (globeMesh === "localTimeMesh") {
      const hour = new Date().getHours();
      setEarthTexture(hour >= 6 && hour < 18 ? dayTexture : nightTexture);
    } else if (globeMesh === "timeZoneMesh") {
      setEarthTexture(dayTexture);
    } else if (globeMesh === "noMesh") {
      setEarthTexture(null);
    }
  }, [globeMesh, dayTexture, nightTexture]);

  return (
    <>
      <ambientLight intensity={globeMesh === "dayMesh" ? 0.6 : 4} />
      <directionalLight position={[5, 5, 5]} intensity={1.2} />
      <pointLight position={[10, -5, -10]} intensity={0.6} />

      <GlobeComponent
        visitedAirports={visitedAirports}
        userSettings={userSettings}
        onAirportClick={onAirportClick || (() => {})}
        earthTexture={earthTexture}
        globeMesh={globeMesh}
      />

      <OrbitControls
        enableZoom
        enablePan={false}
        dampingFactor={0.05}
        minDistance={3.5}
        maxDistance={20}
      />
    </>
  );
}

// ================= Globe - MAIN EXPORT =================
export function Globe({
  visitedAirports,
  onAirportClick,
  globeMesh,
  userSettings,
}: GlobeProps) {
  return (
    <div className="w-full h-full flex items-center justify-center">
      <Canvas
        className="w-full h-full"
        camera={{ position: [0, 0, 6], fov: 80 }}
      >
        <Suspense
          fallback={
            <Html center>
              <div style={{ textAlign: "center" }}>
                <div style={{}} />
                <div>Loading Globe...</div>
              </div>
            </Html>
          }
        >
          <GlobeWithTextures
            visitedAirports={visitedAirports}
            userSettings={userSettings}
            onAirportClick={onAirportClick}
            globeMesh={globeMesh}
          />
        </Suspense>
      </Canvas>
    </div>
  );
}
